import { useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { fadeUp, stagger } from "../lib/motion";

const FAQS = [
  {
    q: "Why not just use AI to build my site?",
    a: "You could — and it would look like everyone else who did. AI makes average easy. What it can't do is position your business, make taste-level design decisions, or take accountability for whether the phone actually rings. We use the best tools available, but the strategy, the standards and the follow-through are human.",
  },
  {
    q: "Why HughLaunch instead of a freelancer?",
    a: "A freelancer builds what you ask for. HughLaunch is responsible for an outcome: one connected system — brand, website, follow-up and visibility — with founder-led strategy and a single person accountable from first call to launch. No handoffs, no project managers in between.",
  },
  {
    q: "Do I need a new website?",
    a: "Not always — and we'll tell you honestly. That's what the free Brand Audit is for. Sometimes repositioning and a few conversion fixes on your current site are the smartest move. We always recommend the smallest change with the biggest impact first.",
  },
  {
    q: "What if I already have branding?",
    a: "Great — we build on it, not over it. The audit looks at how your brand performs, not just how it looks. If it's working, we amplify it across your site and acquisition system. If it's quietly costing you clients, we'll show you exactly where.",
  },
  {
    q: "How quickly can I launch?",
    a: "The Starter foundation goes live in 24–48 hours, the Cinematic Website in about 3 days, and Signature Presence in about 4. Brand Authority is a deeper transformation, so its timeline is set together after your strategy session.",
  },
  {
    q: "What happens after launch?",
    a: "You're not abandoned. Every plan includes your own client portal to track everything, and the growth plans include hands-on management — monitoring, tuning, analytics and updates. Signature includes your first month; Authority includes two months plus weekly strategy calls.",
  },
];

function FaqItem({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div variants={fadeUp} className="border-b border-[#2A2D34]" data-testid={`faq-item-${i}`}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-toggle-${i}`}
        className="flex w-full items-center justify-between gap-6 py-6 text-left group"
      >
        <span className="text-lg lg:text-xl font-bold tracking-tight text-white group-hover:text-[#C6FF00] transition-colors">{q}</span>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#2A2D34] transition-all duration-300 ${open ? "rotate-45 border-[#C6FF00] bg-[#C6FF00]" : "group-hover:border-[#C6FF00]"}`}>
          <Plus className={`h-4 w-4 ${open ? "text-[#0A0C0F]" : "text-[#C6FF00]"}`} strokeWidth={3} />
        </span>
      </button>
      <div className={`grid transition-all duration-400 ease-out ${open ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"}`}>
        <p className="overflow-hidden text-[#8A939B] font-medium leading-relaxed max-w-3xl">{a}</p>
      </div>
    </motion.div>
  );
}

export const FAQ = () => (
  <section className="relative py-24 lg:py-32 panel-grid" data-testid="faq-section">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="lg:col-span-4">
        <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.3em] text-[#C6FF00] mb-5">
          / Fair Questions
        </motion.p>
        <motion.h2 variants={fadeUp} className="text-4xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
          Asking yourself these? Good.
        </motion.h2>
        <motion.p variants={fadeUp} className="mt-6 text-[#8A939B] font-medium leading-relaxed">
          You should interrogate anyone touching your brand. Here are the straight answers.
        </motion.p>
      </motion.div>
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="lg:col-span-8 border-t border-[#2A2D34]">
        {FAQS.map((f, i) => (
          <FaqItem key={f.q} q={f.q} a={f.a} i={i} />
        ))}
      </motion.div>
    </div>
  </section>
);
